// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { constant, identity } from 'lodash';

// Utils
import { updateElement, updateElementIfNotDeleted } from '../actions/elementActions';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getTaskListUpdateChanges } from './taskListPersistenceUtils';

// Actions
import { deselectAllElements } from '../selection/selectionActions';
import {
    goToPreviousTask,
    goToNextTask,
    indentTask,
    unIndentTask,
    moveTaskToTrash,
    onSubmitTaskTitle,
    joinToPreviousTask,
    joinNextTaskToThis,
} from './taskListActions';

// Components
import suggestDeleteTasksDecorator from './suggestion/deleteTasks/suggestDeleteTasksDecorator';
import suggestTitleDecorator from './suggestTitleDecorator';
import taskListDataDecorator from './taskListDataDecorator';
import elementLineEdgeDropTarget from '../line/elementLineEdgeDropTarget';
import TaskList from './TaskList';

// Constants
import { SHOW_TITLE_PROPERTY } from '../../../common/taskLists/taskListConstants';
import { IGNORE_UNDO_REDO_TRANSACTION_ID } from '../../utils/undoRedo/undoRedoConstants';
import groupResizeStateDecorator from '../resizing/groupResizeStateDecorator';
import groupResizeBehaviourDecorator from '../resizing/groupResizeBehaviourDecorator';

const mapDispatchToProps = (dispatch) => ({
    dispatchMoveTaskToTrash: (props) => dispatch(moveTaskToTrash(props)),
    dispatchIndentTask: (props) => dispatch(indentTask(props)),
    dispatchUnIndentTask: (props) => dispatch(unIndentTask(props)),
    dispatchGoToPreviousTask: (props) => dispatch(goToPreviousTask(props)),
    dispatchGoToNextTask: (props) => dispatch(goToNextTask(props)),
    dispatchUpdateTitle: ({ id, title, transactionId }) =>
        dispatch(updateElement({ id, changes: { title }, transactionId })),
    dispatchHideTitle: ({ id, transactionId }) =>
        dispatch(updateElement({ id, changes: { [SHOW_TITLE_PROPERTY]: false }, transactionId })),
    dispatchDeselectTaskList: ({ stopEditing }) => {
        stopEditing && stopEditing();
        dispatch(deselectAllElements());
    },
    dispatchSubmitTaskTitle: ({ elementId }) => dispatch(onSubmitTaskTitle({ taskListId: elementId })),
    dispatchJoinToPreviousTask: (props) => dispatch(joinToPreviousTask(props)),
    dispatchJoinNextTaskToThis: (props) => dispatch(joinNextTaskToThis(props)),
    dispatchUpdateElement: ({ id, changes, transactionId, sync }) =>
        dispatch(updateElement({ id, changes, transactionId, sync })),
    dispatchSaveTextContent: ({ id, changes, ...rest }) =>
        dispatch(updateElementIfNotDeleted({ id, changes, ...rest })),
});

@elementLineEdgeDropTarget
@taskListDataDecorator
@connect(null, mapDispatchToProps)
@suggestTitleDecorator
@suggestDeleteTasksDecorator
@groupResizeStateDecorator()
@groupResizeBehaviourDecorator(constant(true))
class TaskListContainer extends React.Component {
    onStartBackspaceHandler = (args) => {
        const { dispatchJoinToPreviousTask } = this.props;
        dispatchJoinToPreviousTask({ ...this.props, ...args });
    };

    onEndDeleteHandler = (args) => {
        const { dispatchJoinNextTaskToThis } = this.props;
        dispatchJoinNextTaskToThis({ ...this.props, ...args });
    };

    moveToTrashHandler = ({ elementId, focusNextTask, forward }) => {
        const { dispatchMoveTaskToTrash } = this.props;
        dispatchMoveTaskToTrash({ ...this.props, elementId, focusNextTask, forward });
    };

    changeIndentationHandler = ({ elementId, increaseIndentation = true, textContent }) => {
        const { dispatchIndentTask, dispatchUnIndentTask } = this.props;

        return increaseIndentation
            ? dispatchIndentTask({ ...this.props, elementId, textContent })
            : dispatchUnIndentTask({ ...this.props, elementId, textContent });
    };

    goToPreviousTaskHandler = ({ elementId }) => {
        const { dispatchGoToPreviousTask } = this.props;
        return dispatchGoToPreviousTask({ ...this.props, elementId });
    };

    goToNextTaskHandler = ({ elementId }) => {
        const { dispatchGoToNextTask } = this.props;
        return dispatchGoToNextTask({ ...this.props, elementId });
    };

    deselectTaskList = () => {
        const { dispatchDeselectTaskList } = this.props;
        dispatchDeselectTaskList(this.props);
    };

    submitTaskTitle = () => {
        const { dispatchSubmitTaskTitle } = this.props;
        dispatchSubmitTaskTitle(this.props);
    };

    saveContent = (textContent, actionArgs) => {
        const { isEditable, element, dispatchSaveTextContent } = this.props;

        if (!isEditable) return;

        // When saving V2 content, we need to find all the label IDs referenced by the
        // taskItem nodes & update the element's labels to match
        const id = getElementId(element);
        const changes = getTaskListUpdateChanges(element, textContent);

        dispatchSaveTextContent({ id, changes, ...actionArgs });
    };

    // This is used to mock an update to the Task List when children elements of Tasks are updated,
    // which is used to check whether or not to show feature suggestions.
    onTaskListUpdatedHandler = () => {
        const { element, dispatchUpdateElement } = this.props;
        dispatchUpdateElement({
            id: getElementId(element),
            changes: {},
            sync: false,
            transactionId: IGNORE_UNDO_REDO_TRANSACTION_ID,
        });
    };

    render() {
        const { connectLineEdgeDropTarget, canConnectLineEdge } = this.props;

        const lineEdgeConnector =
            connectLineEdgeDropTarget && canConnectLineEdge ? connectLineEdgeDropTarget : identity;

        return lineEdgeConnector(
            <div>
                <TaskList
                    {...this.props}
                    saveContent={this.saveContent}
                    submitTitle={this.submitTaskTitle}
                    deselectTaskList={this.deselectTaskList}
                    moveToTrashHandler={this.moveToTrashHandler}
                    changeIndentationHandler={this.changeIndentationHandler}
                    goToPreviousTaskHandler={this.goToPreviousTaskHandler}
                    goToNextTaskHandler={this.goToNextTaskHandler}
                    onStartBackspaceHandler={this.onStartBackspaceHandler}
                    onEndDeleteHandler={this.onEndDeleteHandler}
                    onTaskListUpdatedHandler={this.onTaskListUpdatedHandler}
                />
            </div>,
        );
    }
}

TaskListContainer.propTypes = {
    element: PropTypes.object.isRequired,
    children: PropTypes.array,
    childElementIds: PropTypes.array,
    currentBoardId: PropTypes.string,
    connectDropTarget: PropTypes.func,
    connectLineEdgeDropTarget: PropTypes.func,
    isHovered: PropTypes.bool,
    isPresentational: PropTypes.bool,
    inList: PropTypes.string,
    canConnectLineEdge: PropTypes.bool,

    dispatchCreateTaskList: PropTypes.func,
    dispatchJoinToPreviousTask: PropTypes.func,
    dispatchJoinNextTaskToThis: PropTypes.func,

    dispatchMoveTaskToTrash: PropTypes.func,
    dispatchIndentTask: PropTypes.func,
    dispatchUnIndentTask: PropTypes.func,
    dispatchGoToPreviousTask: PropTypes.func,
    dispatchGoToNextTask: PropTypes.func,
    dispatchDeselectTaskList: PropTypes.func,
    dispatchSubmitTaskTitle: PropTypes.func,
    dispatchUpdateElement: PropTypes.func,
    dispatchSaveTextContent: PropTypes.func,

    isEditable: PropTypes.bool,
};

export default TaskListContainer;
